import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, Plus, Trash2, ImageIcon, X, Loader2, MoreVertical, Pencil, FolderPlus, Folder, GripVertical, Bold, Italic, Underline, Code, FileCode, Link as LinkIcon, ChevronDown, Home, Edit2, ChevronRight, List, ListOrdered, CheckSquare, Pin } from 'lucide-react';
import { useStoryboardNotes } from '../hooks/useStoryboardNotes';
import { useStoryboards } from '../hooks/useStoryboards';
import ConfirmationModal from '../components/modals/ConfirmationModal';
import StoryboardModal from '../components/modals/StoryboardModal';
import Skeleton from '../components/common/Skeleton';
import { TAG_COLORS, TAG_COLORS_LIGHT } from '../components/common/NoteEntryCard';

const API_BASE = 'https://api.cloudnine.biz.id';
const MAX_IMAGES = 4;

/* ============================================================
   Helper Functions
   ============================================================ */
const parseImages = (img) => {
    if (!img) return [];
    if (Array.isArray(img)) return img;
    try {
        const parsed = JSON.parse(img);
        return Array.isArray(parsed) ? parsed : [img];
    } catch {
        return img ? [img] : [];
    }
};

const serializeImages = (arr) => {
    if (!arr || arr.length === 0) return null;
    return JSON.stringify(arr);
};

const uploadFileGlobal = async (file) => {
    const formData = new FormData();
    formData.append('image', file);
    const res = await fetch(`${API_BASE}/upload.php`, { method: 'POST', body: formData });
    const data = await res.json();
    if (!data.success) throw new Error(data.error || 'Upload failed');
    return data.url;
};

const fetchLinkPreview = async (url) => {
    try {
        const res = await fetch(`${API_BASE}/link_preview.php?url=${encodeURIComponent(url)}`);
        const data = await res.json();
        return data; // API returns the metadata object directly
    } catch { return null; }
};

/* ============================================================
   Styles
   ============================================================ */
const detailStyles = `
    .sb-masonry { columns: 1; column-gap: 1rem; }
    @media (min-width: 640px) { .sb-masonry { columns: 2; } }
    @media (min-width: 1024px) { .sb-masonry { columns: 4; } }
    .sb-masonry-item { break-inside: avoid; margin-bottom: 1rem; }
    .sb-note-card { transition: box-shadow 0.2s, transform 0.15s; }
    .sb-note-card:hover { transform: translateY(-2px); box-shadow: 0 8px 25px rgba(0,0,0,0.12); }
    .sb-note-card.drag-over { outline: 2px dashed #6366f1; outline-offset: -2px; background: rgba(99, 102, 241, 0.05) !important; }
    @keyframes sb-card-enter { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
    .sb-card-enter { animation: sb-card-enter 0.3s ease-out; }
    .sb-img-thumb { width: 100%; aspect-ratio: 1/1; border-radius: 6px; object-fit: cover; cursor: pointer; transition: transform 0.15s, box-shadow 0.15s; }
    .sb-img-thumb:hover { transform: scale(1.02); box-shadow: 0 4px 12px rgba(0,0,0,0.15); }
    .sb-rich code { font-family: 'Fira Code', 'Courier New', monospace; font-size: 0.85em; padding: 0.15em 0.4em; border-radius: 4px; background: rgba(99,102,241,0.1); color: #818cf8; transition: background 0.2s; position: relative; }
    .dark .sb-rich code { background: rgba(99,102,241,0.2); color: #a5b4fc; }
    .sb-rich pre { margin: 0.4em 0; border-radius: 6px; overflow-x: auto; background: rgba(99,102,241,0.05); border: 1px solid rgba(99,102,241,0.1); }
    .sb-rich pre code { display: block; padding: 0.8em; font-size: 0.85em; line-height: 1.5; background: transparent; color: #818cf8; white-space: pre-wrap; word-break: break-word; }
    .dark .sb-rich pre code { color: #a5b4fc; }
    .sb-rich .sb-marker { opacity: 0.4; font-weight: normal; display: none; }
    .sb-editor-active .sb-rich .sb-marker { display: inline; opacity: 0.4; }
    /* Fix for leading empty line: hide BR immediately following a marker in view mode */
    .sb-rich:not(.sb-editor-active) .sb-marker + br { display: none; }
    .sb-rich pre code:not(.sb-editor-active *) { padding-top: 0.4em; }
    .sb-rich ul { list-style: disc; padding-left: 1.2em; margin: 0.25em 0; }
    .sb-rich ol { list-style: decimal; padding-left: 1.2em; margin: 0.25em 0; }
    .sb-rich li { margin: 0.1em 0; }
    .sb-rich ul.sb-checklist { list-style: none; padding-left: 0.2em; }
    .sb-rich ul.sb-checklist li { display: flex; align-items: flex-start; gap: 6px; padding: 2px 0; cursor: default; }
    .sb-rich ul.sb-checklist li::before { content: ''; display: inline-block; width: 14px; height: 14px; min-width: 14px; border: 2px solid #94a3b8; border-radius: 3px; margin-top: 2px; cursor: pointer; transition: all 0.15s; background: transparent; }
    .sb-rich ul.sb-checklist li.checked::before { background: #6366f1; border-color: #6366f1; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='9' height='9' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='4' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='20 6 9 17 4 12'%3E%3C/polyline%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: center; }
    .sb-rich ul.sb-checklist li.checked { opacity: 0.5; text-decoration: line-through; }
    .sb-copy-toast { position: fixed; bottom: 20px; left: 50%; transform: translateX(-50%); background: #10b981; color: white; padding: 4px 12px; border-radius: 20px; font-size: 10px; font-weight: bold; z-index: 10000; animation: sb-toast-in 0.3s ease-out; }
    @keyframes sb-toast-in { from { opacity: 0; transform: translate(-50%, 10px); } to { opacity: 1; transform: translate(-50%, 0); } }
    
    @keyframes sb-highlight-pulse {
        0% { box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.4); border-color: #6366f1; }
        50% { box-shadow: 0 10px 30px rgba(99, 102, 241, 0.3), 0 0 0 10px rgba(99, 102, 241, 0); border-color: #6366f1; }
        100% { box-shadow: 0 0 0 0 rgba(99, 102, 241, 0); border-color: #6366f1; }
    }
    .sb-highlight-item { animation: sb-highlight-pulse 2s ease-in-out infinite; border-color: #6366f1 !important; z-index: 40; position: relative; }
    .sb-mention-link { cursor: pointer; text-decoration: none !important; }
    .sb-mention-link:hover { opacity: 0.8; }
`;

/* ============================================================
   Sub-Components
   ============================================================ */

/** Image Preview Modal */
const ImagePreviewModal = ({ src, onClose }) => {
    if (!src) return null;
    return createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
            <div className="max-w-[90vw] max-h-[90vh] relative" onClick={(e) => e.stopPropagation()}>
                <img src={src} alt="" className="max-w-full max-h-[85vh] rounded-xl shadow-2xl object-contain" />
                <button onClick={onClose} className="absolute -top-3 -right-3 w-7 h-7 rounded-full bg-black/80 text-white flex items-center justify-center shadow-lg"><X size={14} /></button>
            </div>
        </div>,
        document.body
    );
};

const parseContentWithPreviews = (html) => {
    if (!html || !html.includes('data-preview')) return { cleanHTML: html || '', previews: [] };
    const wrap = document.createElement('div'); wrap.innerHTML = html;
    const previews = [];
    wrap.querySelectorAll('[data-preview]').forEach(el => {
        try { previews.push(JSON.parse(el.getAttribute('data-preview'))); } catch { }
        el.remove();
    });
    return { cleanHTML: wrap.innerHTML, previews };
};

const LinkPreviewCard = ({ preview, isDarkMode, onPreviewImage }) => {
    const handleOpen = (e) => {
        e.stopPropagation();
        window.open(preview.url, '_blank', 'noopener,noreferrer');
    };

    return (
        <div className={`rounded-xl border overflow-hidden mt-1.5 shadow-sm transition-all hover:shadow-md ${isDarkMode ? 'bg-slate-800 border-slate-700/60' : 'bg-slate-50 border-slate-200'}`}>
            {preview.image && (
                <img
                    src={preview.image} alt=""
                    className="w-full h-32 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={(e) => { e.stopPropagation(); onPreviewImage?.(preview.image); }}
                />
            )}
            <div className="p-2.5 cursor-pointer group" onClick={handleOpen}>
                <div className={`text-[9px] font-bold uppercase tracking-wider flex items-center gap-1.5 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-500'}`}>
                    {preview.site_name || new URL(preview.url).hostname}
                    <LinkIcon size={8} />
                </div>
                <div className={`text-[11px] font-semibold leading-tight mt-0.5 group-hover:underline transition-all ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                    {preview.title || preview.url}
                </div>
            </div>
        </div>
    );
};

/** Rich Text Toolbar */
const RichToolbar = ({ onAction, isDarkMode, activeStates = {}, onAddImage, uploading = false }) => {
    const fileRef = useRef(null);
    const btnClass = (isActive) => `p-1.5 rounded transition-colors ${isActive
        ? 'bg-indigo-600/20 text-indigo-400'
        : (isDarkMode ? 'hover:bg-slate-700/50 text-slate-400' : 'hover:bg-slate-100 text-slate-500')
        }`;
    return (
        <div className="flex items-center gap-0.5 mb-2 pb-2 border-b border-slate-100/10 flex-wrap">
            <button onMouseDown={(e) => e.preventDefault()} onClick={() => onAction('bold')} className={btnClass(activeStates.bold)} title="Bold"><Bold size={11} /></button>
            <button onMouseDown={(e) => e.preventDefault()} onClick={() => onAction('italic')} className={btnClass(activeStates.italic)} title="Italic"><Italic size={11} /></button>
            <button onMouseDown={(e) => e.preventDefault()} onClick={() => onAction('underline')} className={btnClass(activeStates.underline)} title="Underline"><Underline size={11} /></button>
            <div className="w-px h-3 bg-slate-200 dark:bg-slate-700 mx-1" />
            <button onMouseDown={(e) => e.preventDefault()} onClick={() => onAction('insertUnorderedList')} className={btnClass(activeStates.ul)} title="Bullet List"><List size={11} /></button>
            <button onMouseDown={(e) => e.preventDefault()} onClick={() => onAction('insertOrderedList')} className={btnClass(activeStates.ol)} title="Numbered List"><ListOrdered size={11} /></button>
            <button onMouseDown={(e) => e.preventDefault()} onClick={() => onAction('checklist')} className={btnClass(activeStates.checklist)} title="Checklist"><CheckSquare size={11} /></button>
            <div className="w-px h-3 bg-slate-200 dark:bg-slate-700 mx-1" />
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { onAddImage(Array.from(e.target.files)); e.target.value = ''; }} />
            <button onMouseDown={(e) => e.preventDefault()} onClick={() => fileRef.current?.click()} className={btnClass(false)} title="Add images" disabled={uploading}>{uploading ? <Loader2 size={11} className="animate-spin" /> : <ImageIcon size={11} />}</button>
        </div>
    );
};

/** Inline Edit with Toolbar support and @Mentions */
const InlineEdit = ({ value, isEditing, setEditing, onSave, onCancel, onAddImage, uploading = false, placeholder, className, multiline = false, isDarkMode, showToolbar = false, onPreviewImage, saveBtnText = 'Save', disableOutsideClick = false, editorId }) => {
    const [activeStates, setActiveStates] = useState({});
    const [fetchingLink, setFetchingLink] = useState(false);
    const [editHTML, setEditHTML] = useState(value || '');
    const [mentions, setMentions] = useState([]);
    const [mentionQuery, setMentionQuery] = useState(null);
    const [mentionIndex, setMentionIndex] = useState(0);
    const [mentionPos, setMentionPos] = useState({ top: 0, left: 0 });

    const editorRef = useRef(null);
    const containerRef = useRef(null);
    const isFirstEdit = useRef(true);
    const fetchTimeout = useRef(null);

    // Fetch mentions for suggestions
    useEffect(() => {
        if (!isEditing) return;
        const fetchMentions = async () => {
            try {
                const res = await fetch('https://api.cloudnine.biz.id/mentions.php');
                const result = await res.json();
                if (result.success) setMentions(result.data);
            } catch (err) { console.error('Mention Fetch Error:', err); }
        };
        fetchMentions();
    }, [isEditing]);

    const filteredMentions = mentionQuery !== null
        ? mentions.filter(m => m.path.toLowerCase().includes(mentionQuery.toLowerCase()) || m.name.toLowerCase().includes(mentionQuery.toLowerCase()))
        : [];

    const checkActiveStates = useCallback(() => {
        if (!editorRef.current) return;
        const sel = window.getSelection();
        if (!sel || !sel.rangeCount) return;
        const parentEl = sel.anchorNode?.parentElement;
        const inPre = !!parentEl?.closest('pre');
        setActiveStates({
            bold: document.queryCommandState('bold'),
            italic: document.queryCommandState('italic'),
            underline: document.queryCommandState('underline'),
            code: !inPre && (document.queryCommandValue('formatBlock') === 'code' || !!parentEl?.closest('code:not(pre code)')),
            codeBlock: inPre,
            link: !!parentEl?.closest('a'),
            ul: document.queryCommandState('insertUnorderedList'),
            ol: document.queryCommandState('insertOrderedList'),
            checklist: !!parentEl?.closest('ul.sb-checklist'),
        });
    }, []);

    const insertMention = (item) => {
        if (!editorRef.current) return;
        const sel = window.getSelection();
        if (!sel.rangeCount) return;
        const range = sel.getRangeAt(0);

        // Remove the @query text
        const textNode = range.startContainer;
        const offset = range.startOffset;
        const textContent = textNode.textContent;
        const lastAt = textContent.lastIndexOf('@', offset - 1);

        if (lastAt !== -1) {
            textNode.textContent = textContent.substring(0, lastAt) + textContent.substring(offset);
            range.setStart(textNode, lastAt);
            range.collapse(true);
        }

        const link = document.createElement('a');
        link.href = `mention://${item.type}/${item.id}`;
        link.className = "sb-mention-link inline font-bold text-indigo-500 dark:text-indigo-400 decoration-transparent hover:underline cursor-pointer";
        link.textContent = item.name;
        link.contentEditable = "false";
        link.setAttribute('data-mention-id', item.id);
        link.setAttribute('data-mention-type', item.type);
        if (item.storyboard_id) link.setAttribute('data-parent-id', item.storyboard_id);

        range.insertNode(link);

        // Add a space after mention
        const after = document.createTextNode('\u00A0');
        link.after(after);

        const newRange = document.createRange();
        newRange.setStart(after, 1);
        newRange.collapse(true);
        sel.removeAllRanges();
        sel.addRange(newRange);

        setMentionQuery(null);
        setEditHTML(editorRef.current.innerHTML);
    };

    const handleKeyDown = (e) => {
        if (mentionQuery !== null) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setMentionIndex(prev => (prev + 1) % Math.max(1, filteredMentions.length));
                return;
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setMentionIndex(prev => (prev - 1 + filteredMentions.length) % Math.max(1, filteredMentions.length));
                return;
            } else if (e.key === 'Enter' || e.key === 'Tab') {
                if (filteredMentions.length > 0) {
                    e.preventDefault();
                    insertMention(filteredMentions[mentionIndex]);
                    return;
                } else {
                    setMentionQuery(null);
                }
            } else if (e.key === 'Escape') {
                e.preventDefault();
                setMentionQuery(null);
                return;
            }
        }

        if (e.key === 'Enter') {
            if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                handleSave();
            } else if (!multiline) {
                e.preventDefault();
                handleSave();
            }
        } else if (e.key === 'Escape') {
            onCancel?.();
            setEditing(false);
        }

        const sel = window.getSelection();
        if (e.key === 'Backspace' && sel?.rangeCount > 0) {
            const range = sel.getRangeAt(0);
            const node = sel.anchorNode;
            const offset = sel.anchorOffset;

            // 1. Smart unwrap code block if backspacing at the start of it
            const parentPre = node?.parentElement?.closest('pre');
            if (parentPre && range.startOffset === 0) {
                const firstChild = parentPre.querySelector('code')?.firstChild;
                if (firstChild?.classList?.contains('sb-marker')) {
                    // Deleting opening marker -> active unwrap
                    e.preventDefault();
                    const codeContent = parentPre.querySelector('code').innerText.replace(/^```|```$/g, '');
                    const newNode = document.createTextNode(codeContent);
                    parentPre.parentNode.replaceChild(newNode, parentPre);
                    const endR = document.createRange();
                    endR.setStart(newNode, 0);
                    endR.collapse(true);
                    sel.removeAllRanges(); sel.addRange(endR);
                    return;
                }
            }

            // 2. Smart unwrap inline code if backspacing right after closing backtick
            // If at ZWS after code
            if (node?.nodeType === 3 && node.textContent === '\u200B' && node.previousSibling?.tagName === 'CODE') {
                const codeEl = node.previousSibling;
                const innerText = codeEl.textContent;
                // If text ends with backtick, unwrap
                if (innerText.endsWith('`')) {
                    e.preventDefault();
                    const cleanText = innerText.substring(0, innerText.length - 1);
                    const newNode = document.createTextNode(cleanText);
                    codeEl.parentNode.replaceChild(newNode, codeEl);
                    node.remove();
                    const r = document.createRange();
                    r.setStart(newNode, newNode.length);
                    r.collapse(true);
                    sel.removeAllRanges(); sel.addRange(r);
                }
            }

            // 3. Mention atomic deletion
            if (node?.nodeType === 3 && offset === 0 && node.previousSibling?.classList?.contains('sb-mention-link')) {
                e.preventDefault();
                node.previousSibling.remove();
            } else if (node?.nodeType === 1 && node.classList?.contains('sb-mention-link')) {
                e.preventDefault();
                node.remove();
            }
        }
    };

    const handleKeyUp = async (e) => {
        checkActiveStates();

        const sel = window.getSelection();
        if (sel?.rangeCount > 0) {
            const node = sel.anchorNode;
            const offset = sel.anchorOffset;

            // Mention Detection
            if (node?.nodeType === 3) {
                const textBefore = node.textContent.substring(0, offset);
                const lastAt = textBefore.lastIndexOf('@');
                if (lastAt !== -1 && (lastAt === 0 || /\s/.test(textBefore[lastAt - 1]))) {
                    const query = textBefore.substring(lastAt + 1);
                    setMentionQuery(query);

                    // Position suggestion box
                    const range = sel.getRangeAt(0).cloneRange();
                    range.setStart(node, lastAt);
                    range.setEnd(node, offset);
                    const rect = range.getBoundingClientRect();
                    const containerRect = containerRef.current.getBoundingClientRect();
                    setMentionPos({
                        top: rect.bottom - containerRect.top - 30
                    });
                } else {
                    setMentionQuery(null);
                }
            }

            // Keyboard Shortcuts for markdown-like formatting
            if (editorRef.current && (e.key === '`' || e.key === ' ' || e.key === 'Enter')) {
                const parentPre = node.parentElement?.closest('pre');
                const parentCode = node.parentElement?.closest('code');

                if (node?.nodeType === 3 && !parentPre && !parentCode) {
                    const txt = node.textContent;
                    const cursorOffset = sel.anchorOffset;
                    const before = txt.substring(0, cursorOffset);

                    // Triple backtick START
                    const tripleMatch = before.match(/(?<!`)```$/);
                    if (tripleMatch) {
                        const pre = document.createElement('pre');
                        const code = document.createElement('code');
                        code.innerHTML = `<span class="sb-marker" contenteditable="false">\`\`\`</span>&#8203;<span class="sb-marker" contenteditable="false">\`\`\`</span>`;
                        pre.appendChild(code);
                        node.textContent = txt.substring(0, Math.max(0, cursorOffset - 3)) + txt.substring(cursorOffset);
                        if (node.textContent.trim().length === 0) node.parentNode.replaceChild(pre, node);
                        else {
                            const r = document.createRange(); r.setStart(node, Math.max(0, cursorOffset - 3)); r.collapse(true); r.insertNode(pre);
                        }
                        const focusR = document.createRange(); focusR.setStart(code.childNodes[1], 1); focusR.collapse(true);
                        sel.removeAllRanges(); sel.addRange(focusR);
                        setEditHTML(editorRef.current.innerHTML);
                        return;
                    }

                    // Inline code
                    const inlineMatch = before.match(/(?<!`)`([^`]+)`$/);
                    if (inlineMatch) {
                        const content = inlineMatch[1];
                        const codeEl = document.createElement('code');
                        codeEl.innerHTML = `<span class="sb-marker" contenteditable="false">\`</span>${content}<span class="sb-marker" contenteditable="false">\`</span>`;
                        const start = cursorOffset - inlineMatch[0].length;
                        node.textContent = txt.substring(0, start) + txt.substring(cursorOffset);
                        const r = document.createRange(); r.setStart(node, start); r.collapse(true); r.insertNode(codeEl);

                        const nextNode = document.createTextNode('\u200B');
                        codeEl.parentNode.insertBefore(nextNode, codeEl.nextSibling);
                        const outR = document.createRange(); outR.setStart(nextNode, 1); outR.collapse(true);
                        sel.removeAllRanges(); sel.addRange(outR);
                        setEditHTML(editorRef.current.innerHTML);
                        return;
                    }
                }
            }
        }

        const html = editorRef.current?.innerHTML || '';
        setEditHTML(html);

        if (fetchTimeout.current) clearTimeout(fetchTimeout.current);
        fetchTimeout.current = setTimeout(async () => {
            const textStr = editorRef.current?.innerText || '';
            const urlMatch = textStr.match(/https?:\/\/[^\s]+/);
            const firstUrl = urlMatch ? urlMatch[0] : null;

            const previewTags = editorRef.current?.querySelectorAll('[data-preview]');
            let currentMetaUrl = null;
            if (previewTags && previewTags.length > 0) {
                try {
                    const meta = JSON.parse(previewTags[0].getAttribute('data-preview'));
                    currentMetaUrl = meta.url;
                } catch { }
            }

            if (!firstUrl) {
                if (previewTags?.length > 0) {
                    previewTags.forEach(t => t.remove());
                    setEditHTML(editorRef.current.innerHTML);
                }
            } else if (firstUrl !== currentMetaUrl) {
                previewTags?.forEach(t => t.remove());
                setFetchingLink(true);
                const meta = await fetchLinkPreview(firstUrl);
                if (meta && editorRef.current) {
                    const tag = document.createElement('div');
                    tag.setAttribute('data-preview', JSON.stringify({ ...meta, url: firstUrl }));
                    tag.contentEditable = 'false';
                    editorRef.current.appendChild(tag);
                    setEditHTML(editorRef.current.innerHTML);
                }
                setFetchingLink(false);
            } else if (previewTags.length > 1) {
                for (let i = 1; i < previewTags.length; i++) previewTags[i].remove();
                setEditHTML(editorRef.current.innerHTML);
            }
        }, 500);
    };

    const handleAction = (cmd, val) => {
        if (!editorRef.current) return;
        editorRef.current.focus();
        if (cmd === 'code') {
            const selection = window.getSelection();
            if (selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                if (activeStates.codeBlock) {
                    const pre = selection.anchorNode?.parentElement?.closest('pre');
                    if (pre) {
                        const div = document.createElement('div'); div.innerHTML = '&#8203;';
                        pre.parentNode.insertBefore(div, pre.nextSibling);
                        range.setStart(div.firstChild, 1); range.collapse(true);
                        selection.removeAllRanges(); selection.addRange(range);
                    }
                } else if (range.toString()) {
                    const code = document.createElement('code');
                    code.textContent = range.toString();
                    range.deleteContents();
                    range.insertNode(code);
                } else if (activeStates.code) {
                    const codeEl = selection.anchorNode.parentElement?.closest('code');
                    if (codeEl) { range.setStartAfter(codeEl); range.collapse(true); const zws = document.createTextNode('\u200B'); range.insertNode(zws); range.setStartAfter(zws); range.collapse(true); selection.removeAllRanges(); selection.addRange(range); }
                } else {
                    const code = document.createElement('code');
                    code.innerHTML = '&#8203;';
                    range.insertNode(code);
                    range.setStart(code.firstChild, 1);
                    range.collapse(true);
                    selection.removeAllRanges();
                    selection.addRange(range);
                }
            }
        } else if (cmd === 'codeBlock') {
            const selection = window.getSelection();
            if (selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                if (activeStates.codeBlock) {
                    const pre = selection.anchorNode?.parentElement?.closest('pre');
                    if (pre) {
                        const div = document.createElement('div');
                        div.textContent = pre.textContent;
                        pre.parentNode.replaceChild(div, pre);
                        const r = document.createRange(); r.selectNodeContents(div); r.collapse(false); selection.removeAllRanges(); selection.addRange(r);
                    }
                } else {
                    const pre = document.createElement('pre');
                    const code = document.createElement('code');
                    const selectedText = range.toString();
                    code.textContent = selectedText || '\u200B';
                    pre.appendChild(code);
                    range.deleteContents();
                    range.insertNode(pre);
                    const r = document.createRange();
                    r.selectNodeContents(code);
                    r.collapse(selectedText ? false : true);
                    selection.removeAllRanges();
                    selection.addRange(r);
                }
            }
        } else if (cmd === 'checklist') {
            document.execCommand('insertUnorderedList');
            const sel = window.getSelection();
            const li = sel.anchorNode?.parentElement?.closest('li');
            if (li) {
                li.parentElement.classList.add('sb-checklist');
            }
        } else { document.execCommand(cmd, false, val); }
        checkActiveStates();
        setEditHTML(editorRef.current.innerHTML);
    };

    const handlePaste = async (e) => {
        const items = e.clipboardData?.items;
        if (!items) return;
        let hasImage = false;
        for (let i = 0; i < items.length; i++) {
            if (items[i].type.indexOf('image') !== -1) {
                const blob = items[i].getAsFile();
                if (blob) { onAddImage?.([blob]); hasImage = true; }
            }
        }
        if (hasImage) { e.preventDefault(); return; }
        e.preventDefault();
        const plainText = e.clipboardData.getData('text/plain');
        if (plainText) {
            const escaped = plainText.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>');
            document.execCommand('insertHTML', false, escaped);
        }
    };

    const handleSave = () => {
        if (!editorRef.current) return;
        onSave(editorRef.current.innerHTML);
        setEditing(false);
    };

    useEffect(() => {
        if (!isEditing) {
            isFirstEdit.current = true;
            return;
        }
        const handleClickOutside = (e) => {
            if (disableOutsideClick) return;
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                onCancel?.();
                setEditing(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isEditing, onCancel, setEditing, disableOutsideClick]);

    useEffect(() => {
        if (isEditing && editorRef.current && isFirstEdit.current) {
            const html = value || '';
            editorRef.current.innerHTML = html;

            // Ensure all loaded mentions are non-editable and have unified style
            editorRef.current.querySelectorAll('.sb-mention-link').forEach(link => {
                link.contentEditable = "false";
                link.className = "sb-mention-link inline font-bold text-indigo-500 dark:text-indigo-400 decoration-transparent hover:underline cursor-pointer";
            });

            setEditHTML(editorRef.current.innerHTML);
            isFirstEdit.current = false;
            const range = document.createRange();
            const sel = window.getSelection();
            range.selectNodeContents(editorRef.current);
            range.collapse(false);
            sel.removeAllRanges();
            sel.addRange(range);
        }
    }, [isEditing, value]);

    if (isEditing) {
        const { previews: livePreviews } = parseContentWithPreviews(editHTML);
        return (
            <div className={`w-full sb-editor-active relative`} ref={containerRef}>
                {showToolbar && <RichToolbar onAction={handleAction} onAddImage={onAddImage} uploading={uploading} isDarkMode={isDarkMode} activeStates={activeStates} />}
                <div className="relative">
                    <div
                        id={editorId}
                        ref={editorRef} contentEditable suppressContentEditableWarning
                        onSelect={checkActiveStates} onPaste={handlePaste}
                        onKeyDown={handleKeyDown} onKeyUp={handleKeyUp}
                        className={`sb-rich outline-none px-1 -mx-1 break-words pb-1 min-h-[3rem] ${isDarkMode ? 'sb-editor-dark' : 'sb-editor-light'} ${className}`}
                    />
                    {mentionQuery !== null && filteredMentions.length > 0 && (
                        <div
                            className={`absolute z-[200] left-0 right-0 w-full max-h-48 overflow-y-auto rounded-xl border shadow-2xl py-2 animate-in fade-in zoom-in-95 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}
                            style={{ top: mentionPos.top }}
                        >
                            <div className="px-3 mb-1 text-[9px] font-bold uppercase tracking-widest opacity-40">Mentions</div>
                            {filteredMentions.map((item, idx) => (
                                <div
                                    key={`${item.type}-${item.id}`}
                                    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); insertMention(item); }}
                                    className={`px-3 py-2 cursor-pointer transition-colors ${idx === mentionIndex ? (isDarkMode ? 'bg-indigo-600 text-white' : 'bg-indigo-50 text-indigo-700') : (isDarkMode ? 'hover:bg-slate-700 text-slate-300' : 'hover:bg-slate-50 text-slate-600')}`}
                                >
                                    <div className="text-[10px] font-bold truncate">{item.name}</div>
                                    <div className={`text-[8px] truncate opacity-50 font-medium`}>{item.path}</div>
                                </div>
                            ))}
                        </div>
                    )}
                    {fetchingLink && (
                        <div className={`absolute bottom-2 right-2 flex items-center gap-1.5 px-2 py-1 rounded-md backdrop-blur-sm border shadow-sm animate-pulse ${isDarkMode ? 'bg-slate-800/80 border-slate-700' : 'bg-white/80 border-slate-200'}`}>
                            <Loader2 size={10} className="animate-spin text-indigo-500" />
                            <span className="text-[8px] font-bold text-slate-500 uppercase tracking-tighter">Fetching...</span>
                        </div>
                    )}
                </div>
                {livePreviews.length > 0 && (
                    <div className="grid grid-cols-1 gap-2 mt-2 opacity-80">
                        {livePreviews.map((p, i) => <LinkPreviewCard key={i} preview={p} isDarkMode={isDarkMode} onPreviewImage={onPreviewImage} />)}
                    </div>
                )}
                <div className="flex justify-end gap-2 mt-3 pt-2 border-t border-slate-100/5">
                    <button onClick={() => { onCancel?.(); setEditing(false); }} className={`px-3 py-1 rounded-lg text-[9px] font-bold ${isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-500'}`}>Cancel</button>
                    <button onClick={handleSave} className="px-3 py-1 rounded-lg bg-emerald-600 text-white text-[9px] font-bold shadow-lg shadow-emerald-500/20">{saveBtnText}</button>
                </div>
            </div>
        );
    }

    const handleViewClick = (e) => {
        const mentionLink = e.target.closest('.sb-mention-link');
        if (mentionLink) {
            e.preventDefault();
            e.stopPropagation();
            const id = mentionLink.getAttribute('data-mention-id');
            const type = mentionLink.getAttribute('data-mention-type');
            const parentId = mentionLink.getAttribute('data-parent-id');
            const event = new CustomEvent('sb-mention-click', {
                detail: { id, type, parentId, name: mentionLink.textContent },
                bubbles: true
            });
            e.target.dispatchEvent(event);
            return;
        }

        if (e.target.tagName === 'CODE') {
            e.stopPropagation();
            navigator.clipboard.writeText(e.target.innerText);
            const toast = document.createElement('div'); toast.className = 'sb-copy-toast'; toast.innerText = 'Copied!'; document.body.appendChild(toast);
            setTimeout(() => toast.remove(), 2000);
            return;
        }
        const li = e.target.closest('ul.sb-checklist li');
        if (li) {
            const liRect = li.getBoundingClientRect();
            if (e.clientX - liRect.left <= 22) {
                e.stopPropagation();
                li.classList.toggle('checked');
                onSave(e.currentTarget.querySelector('[data-view-content]').innerHTML);
            }
        }
    };

    const { cleanHTML, previews } = parseContentWithPreviews(value);
    return (
        <div onClick={handleViewClick} className={`sb-rich rounded px-1 -mx-1 transition-colors break-words ${isDarkMode ? 'text-slate-300' : 'text-slate-600'} ${className}`}>
            <div data-view-content dangerouslySetInnerHTML={{ __html: cleanHTML }} />
            {previews.map((p, i) => <LinkPreviewCard key={i} preview={p} isDarkMode={isDarkMode} onPreviewImage={onPreviewImage} />)}
        </div>
    );
};

/** Note Item with Drag/Drop state */
const NoteItem = ({ item, cardId, onUpdate, onDelete, onDragStart, draggingId, isDarkMode, onPreviewImage, isNewlyAdded }) => {
    const [uploading, setUploading] = useState(false);
    const [isEditing, setIsEditing] = useState(isNewlyAdded || false);
    const [localImages, setLocalImages] = useState([]);
    const [showMenu, setShowMenu] = useState(false);
    const itemRef = useRef(null);
    const menuRef = useRef(null);

    // Auto-scroll into view if newly added
    useEffect(() => {
        if (isNewlyAdded && itemRef.current) {
            itemRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [isNewlyAdded]);

    // Close menu on outside click
    useEffect(() => {
        if (!showMenu) return;
        const handleClick = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false);
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [showMenu]);

    // Sync local images when editing starts or data changes externally
    useEffect(() => {
        if (!isEditing) setLocalImages(parseImages(item.image));
    }, [item.image, isEditing]);

    const handleAddImages = async (files) => {
        if (!files.length) return;
        setUploading(true);
        try {
            const urls = await Promise.all(files.map(f => uploadFileGlobal(f)));
            setLocalImages(prev => [...prev, ...urls].slice(0, MAX_IMAGES));
        } catch (err) { console.error('Upload Error:', err); } finally { setUploading(false); }
    };

    const handleRemoveImage = (e, index) => {
        e.stopPropagation();
        setLocalImages(prev => prev.filter((_, i) => i !== index));
    };

    const handleFinalSave = async (newContent) => {
        await onUpdate(item.id, {
            content: newContent,
            image: serializeImages(localImages)
        });
    };

    const handleCancel = () => {
        if (isNewlyAdded && (!item.content || item.content === '')) {
            onDelete(item.id, cardId, true);
        }
    };

    return (
        <div ref={itemRef} className={`group flex items-start gap-2 py-2.5 px-3 border-b border-slate-100/5 ${isDarkMode ? 'hover:bg-slate-700/30' : 'hover:bg-slate-50/50'} ${draggingId === item.id ? 'opacity-30 grayscale' : ''}`}>
            <div draggable onDragStart={(e) => onDragStart(e, item.id, cardId)} className="mt-1 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-40 transition-opacity">
                <GripVertical size={12} className="text-slate-400" />
            </div>
            <div className="flex-1 min-w-0">
                <InlineEdit
                    value={item.content} isEditing={isEditing} setEditing={setIsEditing}
                    onSave={handleFinalSave}
                    onCancel={handleCancel}
                    onAddImage={handleAddImages} uploading={uploading}
                    multiline isDarkMode={isDarkMode} showToolbar
                    className="text-[11px] leading-relaxed" placeholder="Add content..." onPreviewImage={onPreviewImage}
                />
                <div className="flex flex-col gap-2 mt-2">
                    {localImages.map((u, i) => (
                        <div key={i} className="relative group/img overflow-hidden rounded-xl border border-slate-100/10 shadow-sm transition-all hover:shadow-md">
                            <img
                                src={u} alt=""
                                className="w-full h-32 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                                onClick={(e) => { e.stopPropagation(); onPreviewImage(u); }}
                            />
                            {isEditing && (
                                <button onClick={(e) => handleRemoveImage(e, i)} className="absolute top-2 right-2 p-1.5 rounded-full bg-rose-500/80 text-white opacity-0 group-hover/img:opacity-100 transition-opacity backdrop-blur-sm">
                                    <X size={10} />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            </div>
            <div className="relative" ref={menuRef}>
                <button onClick={() => setShowMenu(!showMenu)} className={`opacity-0 group-hover:opacity-100 p-1 transition-all ${isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-400 hover:text-slate-600'}`}>
                    <MoreVertical size={12} />
                </button>
                {showMenu && (
                    <div className={`absolute right-0 top-6 z-50 min-w-[100px] rounded-lg shadow-xl border py-1 animate-in fade-in zoom-in-95 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                        <button onClick={() => { setShowMenu(false); setIsEditing(true); }} className={`w-full text-left px-3 py-1.5 text-[10px] font-medium flex items-center gap-2 ${isDarkMode ? 'hover:bg-slate-700 text-slate-300' : 'hover:bg-slate-50 text-slate-600'}`}>
                            <Pencil size={10} /> Edit
                        </button>
                        <button onClick={() => { setShowMenu(false); onDelete(item.id, cardId); }} className={`w-full text-left px-3 py-1.5 text-[10px] font-medium flex items-center gap-2 text-rose-500 ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-rose-50'}`}>
                            <Trash2 size={10} /> Delete
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

/** Original SB Note Card (Kanban) */
const SBNoteCard = ({ card, onUpdate, onDelete, onAddItem, onDragStart, draggingId, onDragOver, dragOverId, onDrop, isDarkMode, onPreviewImage, newlyAddedId, onTogglePin, isHighlighted }) => {
    const isOver = dragOverId === card.id;
    const is_pinned = !!card.is_pinned;
    return (
        <div
            className={`sb-masonry-item sb-note-card rounded-2xl border transition-all ${isHighlighted ? 'sb-highlight-item' : ''}`}
            onDragOver={(e) => { e.preventDefault(); onDragOver(card.id); }}
            onDragLeave={() => onDragOver(null)}
            onDrop={(e) => { onDragOver(null); onDrop(card.id); }}
        >
            <div
                className={`rounded-xl border flex flex-col transition-all duration-200
                    ${isOver ? `ring-2 ring-indigo-500 ring-offset-2 bg-indigo-50/10 ${isDarkMode ? 'ring-offset-slate-900' : ''}` : ''}
                    ${isDarkMode ? 'bg-slate-800/80 border-slate-700/50' : 'bg-white border-slate-200 shadow-sm'}
                    ${is_pinned ? (isDarkMode ? 'border-indigo-500/30 bg-indigo-500/5' : 'border-indigo-200 bg-indigo-50/30') : ''}
                `}>
                <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-slate-100/10">
                    <InlineEdit value={card.title} isEditing={false} setEditing={() => { }} onSave={(t) => onUpdate(card.id, { title: t })} placeholder="Card title..." className="text-xs font-semibold flex-1" isDarkMode={isDarkMode} />
                    <div className="flex gap-1 ml-2 shrink-0">
                        <button onClick={() => onTogglePin(card.id)} className={`p-1 rounded transition-colors ${is_pinned ? 'text-indigo-500' : (isDarkMode ? 'text-slate-400 hover:bg-slate-700' : 'text-slate-400 hover:bg-slate-100')}`}>
                            <Pin size={12} fill={is_pinned ? "currentColor" : "none"} />
                        </button>
                        <button onClick={() => onAddItem(card.id, { content: '' })} className="p-1 rounded hover:bg-indigo-500/10 text-slate-400 hover:text-indigo-400 transition-colors"><Plus size={12} /></button>
                        <button onClick={() => onDelete(card.id)} className="p-1 rounded hover:bg-rose-500/10 text-slate-400 hover:text-rose-400 transition-colors"><Trash2 size={12} /></button>
                    </div>
                </div>
                <div className="max-h-[480px] overflow-y-auto scrollbar-hide">
                    {(card.children || []).map(item => (
                        <NoteItem
                            key={item.id} item={item} cardId={card.id} onUpdate={onUpdate} onDelete={onDelete}
                            onDragStart={onDragStart} draggingId={draggingId} isDarkMode={isDarkMode}
                            onPreviewImage={onPreviewImage}
                            isNewlyAdded={item.id === newlyAddedId}
                        />
                    ))}
                </div>
            </div>
            <style dangerouslySetInnerHTML={{ __html: '.scrollbar-hide::-webkit-scrollbar { display: none; } .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }' }} />
        </div>
    );
};

/* ============================================================
   Create Kanban Card Modal
   ============================================================ */
const CreateCardModal = ({ onClose, onSave, isDarkMode, onPreviewImage }) => {
    const [title, setTitle] = useState('');
    const [images, setImages] = useState([]);
    const [uploading, setUploading] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        setTimeout(() => inputRef.current?.focus(), 50);
    }, []);

    const handleAddImages = async (files) => {
        if (!files.length) return;
        setUploading(true);
        try {
            const urls = await Promise.all(files.map(f => uploadFileGlobal(f)));
            setImages(prev => [...prev, ...urls].slice(0, MAX_IMAGES));
        } catch (err) { console.error('Upload Error:', err); } finally { setUploading(false); }
    };

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in p-4" onClick={onClose}>
            <div className={`w-full max-w-2xl max-h-[90vh] flex flex-col rounded-2xl shadow-2xl border ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`} onClick={e => e.stopPropagation()}>

                <div className={`flex justify-between items-center p-4 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-100'}`}>
                    <h3 className="text-sm font-bold flex items-center gap-2"><Plus size={16} className="text-emerald-500" /> New Kanban Card</h3>
                    <button onClick={onClose} className={`p-1.5 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}>
                        <X size={16} />
                    </button>
                </div>

                <div className="p-4 overflow-y-auto flex-1 flex flex-col gap-4">
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-widest opacity-40 mb-1.5 block">Card Title</label>
                        <input
                            ref={inputRef}
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Tab' && !e.shiftKey) {
                                    e.preventDefault();
                                    document.getElementById('create-modal-editor')?.focus();
                                }
                            }}
                            placeholder="Enter card name..."
                            className={`w-full px-4 py-2.5 rounded-xl text-xs font-medium outline-none border transition-colors ${isDarkMode ? 'bg-slate-900 border-slate-700 focus:border-indigo-500 text-slate-200' : 'bg-slate-50 border-slate-200 focus:border-indigo-500 text-slate-800'}`}
                        />
                    </div>

                    <div className="flex-1 flex flex-col min-h-[250px]">
                        <label className="text-[10px] font-bold uppercase tracking-widest opacity-40 mb-1.5 block">Initial Note (Optional)</label>
                        <div className={`flex-1 flex flex-col border rounded-xl overflow-hidden ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                            <div className="h-full overflow-y-auto p-2">
                                <InlineEdit
                                    value=""
                                    isEditing={true}
                                    setEditing={() => { }}
                                    onSave={(content) => {
                                        if (!title.trim()) { alert("Please provide a Kanban Card Title"); return; }
                                        onSave(title.trim(), content, images);
                                    }}
                                    onCancel={onClose}
                                    onAddImage={handleAddImages}
                                    uploading={uploading}
                                    multiline
                                    disableOutsideClick={true}
                                    editorId="create-modal-editor"
                                    isDarkMode={isDarkMode}
                                    showToolbar
                                    saveBtnText="Create Card & Note"
                                    className="text-[11px] leading-relaxed min-h-[120px]"
                                    placeholder="Write the first note for this card..."
                                    onPreviewImage={onPreviewImage}
                                />
                            </div>
                        </div>

                        {images.length > 0 && (
                            <div className="grid grid-cols-2 xs:grid-cols-3 sm:grid-cols-4 gap-2 mt-3">
                                {images.map((u, i) => (
                                    <div key={i} className={`relative group/img overflow-hidden rounded-xl border shadow-sm aspect-square ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                                        <img src={u} alt="" className="w-full h-full object-cover cursor-pointer hover:opacity-90" onClick={() => onPreviewImage(u)} />
                                        <button onClick={() => setImages(prev => prev.filter((_, idx) => idx !== i))} className="absolute top-2 right-2 p-1.5 rounded-full bg-rose-500/80 text-white opacity-0 group-hover/img:opacity-100 transition-opacity backdrop-blur-sm"><X size={10} /></button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

/* ============================================================
   Main Component
   ============================================================ */
const NoteDetailContent = ({ storyboard, isDarkMode, onBack }) => {
    const [currentSBId, setCurrentSBId] = useState(storyboard?.id);
    const [sbHistory, setSbHistory] = useState([storyboard]);
    const [visitHistory, setVisitHistory] = useState([storyboard.id]);

    const { storyboards: subStoryboards, addStoryboard, tags, addTag, updateStoryboard, deleteStoryboard, togglePinStoryboard } = useStoryboards(currentSBId);
    const { notes, loading, addCard, addItem, updateNote, deleteNote, togglePinNote, moveItem } = useStoryboardNotes(currentSBId);

    const [showAddMenu, setShowAddMenu] = useState(false);
    const [addingType, setAddingType] = useState(null);
    const [editingSB, setEditingSB] = useState(null);
    const [newNoteId, setNewNoteId] = useState(null);
    const [highlightedId, setHighlightedId] = useState(null);
    const [newName, setNewName] = useState('');
    const [draggingId, setDraggingId] = useState(null);
    const [dragOverId, setDragOverId] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null });

    // Clear new note ID when navigating to prevent ghost auto-edits
    useEffect(() => {
        setNewNoteId(null);
        // Clear highlights on manual navigation
        setHighlightedId(null);
    }, [currentSBId]);

    // Global click listener for mentions (handled via custom event)
    useEffect(() => {
        const handleMentionClick = async (e) => {
            const { id, type, parentId, name } = e.detail;

            const targetSBId = type === 'storyboard' ? id : parentId;
            if (targetSBId && String(targetSBId) !== String(currentSBId)) {
                try {
                    // Fetch full lineage for accurate breadcrumbs
                    const res = await fetch(`${API_BASE}/storyboards.php?id=${targetSBId}&action=ancestors`);
                    const result = await res.json();
                    if (result.success && result.data) {
                        setSbHistory(result.data);
                        setCurrentSBId(targetSBId);
                        setVisitHistory(prev => [...prev, targetSBId]);
                    }
                } catch (err) {
                    console.error('Failed to reconstruct path:', err);
                    setCurrentSBId(targetSBId);
                    setSbHistory([{ id: targetSBId, name: name || 'Storyboard' }]);
                    setVisitHistory(prev => [...prev, targetSBId]);
                }
            }

            if (type === 'card' || type === 'note') {
                setHighlightedId(id);
                setTimeout(() => setHighlightedId(null), 8000);
            }
        };

        window.addEventListener('sb-mention-click', handleMentionClick);
        return () => window.removeEventListener('sb-mention-click', handleMentionClick);
    }, [currentSBId, storyboard]);

    const currentSB = sbHistory[sbHistory.length - 1] || storyboard;

    const goHome = () => {
        setSbHistory([storyboard]);
        setCurrentSBId(storyboard.id);
        setVisitHistory(prev => [...prev, storyboard.id]);
        onBack?.();
    };

    const navigateBackInHistory = () => {
        if (visitHistory.length > 1) {
            const newHistory = visitHistory.slice(0, -1);
            const prevId = newHistory[newHistory.length - 1];
            setVisitHistory(newHistory);

            // Reconstruct breadcrumb for this location too!
            fetch(`${API_BASE}/storyboards.php?id=${prevId}&action=ancestors`)
                .then(r => r.json())
                .then(res => {
                    if (res.success) setSbHistory(res.data);
                });

            setCurrentSBId(prevId);
        } else {
            onBack?.();
        }
    };

    const handleAddItemWrapper = async (cardId, initialContent) => {
        const newItem = await addItem(cardId, initialContent);
        if (newItem) {
            setNewNoteId(newItem.id);
            // Clear the "newness" after a delay so it doesn't trigger again on re-mounts
            setTimeout(() => {
                setNewNoteId(prev => prev === newItem.id ? null : prev);
            }, 1000);
        }
    };

    const handleCreate = async () => {
        if (!newName.trim()) return;
        if (addingType === 'card') await addCard(newName);
        setNewName(''); setAddingType(null);
    };

    const handleCreateSubSB = async (data) => {
        await addStoryboard({ ...data, parent_id: currentSBId });
        setAddingType(null);
    };

    const handleDragStart = (e, id, fromId) => {
        setDraggingId(id);
        e.dataTransfer.setData('application/json', JSON.stringify({ itemId: id, fromCardId: fromId }));
    };

    const handleDrop = async (e, toId) => {
        e.preventDefault();
        try {
            const data = JSON.parse(e.dataTransfer.getData('application/json'));
            if (data.fromCardId !== toId) await moveItem(data.itemId, data.fromCardId, toId);
        } catch { } finally { setDraggingId(null); }
    };

    return (
        <div className="space-y-6 animate-fade-in pb-20">
            <style>{detailStyles}</style>

            <div className="flex flex-col gap-3">
                <button onClick={navigateBackInHistory} className={`flex items-center gap-2 w-fit text-xs transition-colors rounded-lg px-2 py-1.5 ${isDarkMode ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-800' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'}`}>
                    <ArrowLeft size={14} /> Back
                </button>
                <div className="flex items-center gap-3 overflow-hidden ml-1">
                    <button onClick={goHome} className={`p-1.5 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-slate-800 text-slate-400 hover:text-indigo-400' : 'hover:bg-slate-100 text-slate-500 hover:text-indigo-500'}`}>
                        <Home size={16} />
                    </button>
                    {sbHistory.map((sb, i) => (
                        <React.Fragment key={i}>
                            <span onClick={() => {
                                setSbHistory(prev => prev.slice(0, i + 1));
                                setCurrentSBId(sb.id);
                                setVisitHistory(prev => [...prev, sb.id]);
                            }} className={`cursor-pointer hover:underline transition-colors ${i === sbHistory.length - 1 ? (isDarkMode ? 'text-indigo-400' : 'text-indigo-600') : 'opacity-50'}`}>{sb.name}</span>
                            {i < sbHistory.length - 1 && <span className="opacity-20 mx-0.5">/</span>}
                        </React.Fragment>
                    ))}
                </div>
            </div>

            <div className={`flex justify-between items-center p-4 rounded-2xl border border-slate-100/10 ${isDarkMode ? 'bg-slate-800/30' : 'bg-slate-100/30'}`}>
                <div><h2 className="text-sm font-bold">{currentSB.name}</h2><p className="text-[10px] opacity-40">Manage your storyboard hierarchy and notes</p></div>
                <div className="relative">
                    <button onClick={() => setShowAddMenu(!showAddMenu)} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-2 shadow-lg shadow-indigo-500/20 transition-all">
                        <Plus size={14} /> Add New <ChevronDown size={14} className={`transition-transform ${showAddMenu ? 'rotate-180' : ''}`} />
                    </button>
                    {showAddMenu && (
                        <div className={`absolute right-0 top-full mt-2 w-48 rounded-2xl border shadow-2xl z-50 overflow-hidden animate-fade-in ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                            <button onClick={() => { setAddingType('folder'); setShowAddMenu(false); }} className={`w-full flex items-center gap-3 px-4 py-3 text-xs transition-colors ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-50'}`}><FolderPlus size={14} className="text-indigo-500" /> New Sub-Storyboard</button>
                            <button onClick={() => { setAddingType('card'); setShowAddMenu(false); }} className={`w-full flex items-center gap-3 px-4 py-3 text-xs transition-colors ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-50'}`}><Plus size={14} className="text-emerald-500" /> New Kanban Card</button>
                        </div>
                    )}
                </div>
            </div>

            {addingType === 'card' && (
                <CreateCardModal
                    isDarkMode={isDarkMode}
                    onClose={() => setAddingType(null)}
                    onSave={async (title, content, imagesArr) => {
                        setAddingType(null);
                        const card = await addCard(title);
                        if (card && (content.replace(/<[^>]*>?/gm, '').trim() || imagesArr.length > 0)) {
                            // Immediately add the note item into the card using serializeImages helper
                            await addItem(card.id, { content, image: serializeImages(imagesArr) });
                        }
                    }}
                    onPreviewImage={setPreviewImage}
                />
            )}

            {loading ? (
                <div className="sb-masonry">
                    {[...Array(6)].map((_, i) => (
                        <div key={i} className="sb-masonry-item">
                            <div className={` rounded-xl border p-4 space-y-3 ${isDarkMode ? 'bg-slate-800/50 border-slate-700/30' : 'bg-white border-slate-100'}`}>
                                <Skeleton className="h-4 w-1/2 rounded" isDarkMode={isDarkMode} />
                                <div className="space-y-1.5">
                                    <Skeleton className="h-3 w-full rounded" isDarkMode={isDarkMode} />
                                    <Skeleton className="h-3 w-5/6 rounded" isDarkMode={isDarkMode} />
                                </div>
                                <div className="flex gap-1.5 pt-1">
                                    <Skeleton className="h-10 w-10 rounded-lg" isDarkMode={isDarkMode} />
                                    <Skeleton className="h-10 w-10 rounded-lg" isDarkMode={isDarkMode} />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="sb-masonry">
                    {(subStoryboards || []).map(sb => (
                        <SubStoryboardCard
                            key={`sb-${sb.id}`} sb={sb} isDarkMode={isDarkMode}
                            onEnter={(s) => { setSbHistory(prev => [...prev, s]); setCurrentSBId(s.id); setVisitHistory(prev => [...prev, s.id]); }}
                            onEdit={(s) => setEditingSB(s)}
                            onDelete={(id) => setConfirmModal({ isOpen: true, title: 'Delete Sub Storyboard', message: 'Delete permanently?', onConfirm: () => deleteStoryboard(id), confirmColor: 'rose' })}
                            onTogglePin={togglePinStoryboard}
                            isHighlighted={sb.id == highlightedId}
                        />
                    ))}
                    {(notes || []).map(card => (
                        <SBNoteCard
                            key={`card-${card.id}`} card={card} isDarkMode={isDarkMode} onUpdate={updateNote}
                            onAddItem={handleAddItemWrapper} onDragStart={handleDragStart} draggingId={draggingId}
                            onDragOver={setDragOverId} dragOverId={dragOverId} onDrop={handleDrop}
                            onDelete={(id, pid) => { setConfirmModal({ isOpen: true, title: pid ? 'Delete Note' : 'Delete Card', message: 'Delete permanently?', onConfirm: () => deleteNote(id), confirmColor: 'rose' }); }}
                            onPreviewImage={setPreviewImage} newlyAddedId={newNoteId}
                            onTogglePin={togglePinNote}
                            isHighlighted={card.id == highlightedId}
                        />
                    ))}
                </div>
            )}

            <StoryboardModal
                isOpen={addingType === 'folder' || !!editingSB}
                onClose={() => { setAddingType(null); setEditingSB(null); }}
                onSave={async (data) => {
                    if (editingSB) await updateStoryboard({ id: editingSB.id, ...data });
                    else await handleCreateSubSB(data);
                }}
                initialData={editingSB}
                isDarkMode={isDarkMode} tags={tags} addTag={addTag}
            />
            <ImagePreviewModal src={previewImage} onClose={() => setPreviewImage(null)} />
            <ConfirmationModal {...confirmModal} onClose={() => setConfirmModal(p => ({ ...p, isOpen: false }))} isDarkMode={isDarkMode} />
        </div>
    );
};


const SubStoryboardCard = ({ sb, onEnter, onEdit, onDelete, onTogglePin, isDarkMode, isHighlighted }) => {
    const sbTags = sb.tags || [];
    const is_pinned = !!sb.is_pinned;
    return (
        <div className={`sb-masonry-item sb-card-enter ${isHighlighted ? 'sb-highlight-item' : ''}`}>
            <div
                className={`p-4 rounded-xl border flex items-center justify-between group transition-all duration-300 hover:shadow-lg hover:-translate-y-1 cursor-pointer
                    ${isDarkMode ? 'bg-slate-800/80 border-slate-700/50 hover:border-indigo-500/50 shadow-slate-900/20' : 'bg-white border-slate-200 hover:border-indigo-400/50 shadow-slate-200/50'}
                    ${is_pinned ? (isDarkMode ? 'border-indigo-500/30 bg-indigo-500/5' : 'border-indigo-200 bg-indigo-50/30') : ''}
                `}
                onClick={() => onEnter(sb)}
            >
                <div className="flex items-center gap-3 min-w-0">
                    <div className={`p-2.5 rounded-lg shrink-0 transition-colors ${is_pinned ? 'bg-indigo-500 text-white shadow-lg shadow-indigo-500/20' : (isDarkMode ? 'bg-slate-700 group-hover:bg-indigo-500/20 text-indigo-400' : 'bg-indigo-50 group-hover:bg-indigo-100 text-indigo-600')}`}>
                        <Folder size={18} />
                    </div>
                    <div className="min-w-0">
                        <div className={`text-xs font-bold truncate ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{sb.name}</div>
                        <div className="flex flex-wrap gap-1 mt-0.5">
                            {sbTags.map(t => {
                                const colors = isDarkMode ? (TAG_COLORS[t.color] || TAG_COLORS.slate) : (TAG_COLORS_LIGHT[t.color] || TAG_COLORS_LIGHT.slate);
                                return <span key={t.id} className={`text-[7px] px-1 py-0.5 rounded font-bold uppercase tracking-tighter border ${colors.bg} ${colors.text} ${colors.border}`}>{t.name}</span>;
                            })}
                        </div>
                    </div>
                </div>
                <div className="flex gap-0.5 ml-2 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
                    <button onClick={() => onTogglePin(sb.id)} className={`p-1.5 rounded-lg transition-colors ${is_pinned ? 'text-indigo-500 bg-indigo-500/10' : (isDarkMode ? 'text-slate-400 hover:bg-slate-700' : 'text-slate-400 hover:bg-slate-100')}`}><Pin size={12} fill={is_pinned ? "currentColor" : "none"} /></button>
                    <button onClick={() => onEdit(sb)} className="p-1.5 rounded-lg hover:bg-indigo-500/10 text-slate-400 hover:text-indigo-400"><Edit2 size={12} /></button>
                    <button onClick={() => onDelete(sb.id)} className="p-1.5 rounded-lg hover:bg-rose-500/10 text-slate-400 hover:text-rose-400"><Trash2 size={12} /></button>
                    <ChevronRight size={14} className="text-slate-300 ml-1" />
                </div>

                {!!is_pinned && (
                    <div className="absolute top-2 right-2 pointer-events-none opacity-40 group-hover:opacity-0 transition-opacity">
                        <Pin size={10} fill="currentColor" className="text-indigo-500 rotate-45" />
                    </div>
                )}
            </div>
        </div>
    );
};

export default NoteDetailContent;
